import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useMap } from 'react-map-gl';
import { Form, Row } from '@components/form/Form/Form';
import { MapMarkerField } from '@components/form/MapMarkerField/MapMarkerField';
import { getFilledFieldsObjOnFormSubmit, isErrorResponse } from '@utils/utils';
import { useTranslation } from 'react-i18next';
import { LoadingBackdrop } from '@components/LoadingBackdrop/LoadingBackdrop';
import {
    cleanRoadRouteCheckPoints,
    ROAD_ROUTE_CHECKER_MARKER_NAME,
} from '@redux/road-route-check/road-route-check-reducer';
import './road-route-checker.scss';
import { useToastContext } from '@context/toastContext';
import { useMapMarkersCoordinates } from '@hooks/useMapMarkersCoordinates';
import { usePresetsContext } from '@context/presetsContext';
import { EntityServiceName, MapboxLayerType } from '@api/enums/enums';
import { entityMapLayersToSourcesAndLayers } from '@utils/mapUtils';
import { presetsApi } from '@api/presetsApi/presetsApi';
import * as TextField from '@components/form/TextField/TextField';
import { addedLayers, addedSourceData, addedSources } from '@redux/map/map-reducer';
import isochroneData from '../../../../isochrone.json';
import { EntityMapLayer } from '@api/entities/replancity_MapLayer';

const IsochronesViewer = ({
    entityServiceName,
    formFields,
}: {
    entityServiceName: EntityServiceName;
    formFields?: React.ReactNode;
}) => {
    const [loading, setLoading] = useState<boolean>(false);

    const formRef = useRef<HTMLFormElement>(null);
    const { selectedPresetId: presetId } = usePresetsContext();
    const { markersCoordinates } = useMapMarkersCoordinates({
        fromPoint: ROAD_ROUTE_CHECKER_MARKER_NAME.fromPoint,
        toPoint: ROAD_ROUTE_CHECKER_MARKER_NAME.toPoint,
    });
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { addToast } = useToastContext();

    const { mapboxMapId } = useMap();

    useEffect(() => {
        return () => {
            dispatch(cleanRoadRouteCheckPoints());
        };
    }, []);

    const onFormSubmit = useCallback(
        async event => {
            const abortController = new AbortController();

            const formDataObj = getFilledFieldsObjOnFormSubmit(event);

            const fromPoint = JSON.parse(formDataObj.fromPoint as string);

            setLoading(true);

            const resp = await presetsApi.calculateIsochrones({
                presetId,
                fromLon: fromPoint.lng,
                fromLat: fromPoint.lat,
                startTime: formDataObj.startTime as string,
                distance: formDataObj.distance as number,
                ...(formDataObj.timePeriod
                    ? { timePeriod: +formDataObj.timePeriod }
                    : { timePeriod: 1 }),
                abortSignal: abortController.signal,
            });

            if (isErrorResponse(resp)) {
                addToast(`${t('route-check.wrong-point-error-toast')}`, 'error');
            } else {
                const map = mapboxMapId?.getMap();

                const isochroneSource: EntityMapLayer = {
                    id: 'isochrones',
                    type: MapboxLayerType.POLYGON,
                    usageType: 'data',
                    displayName: 'Isochrones',
                    viewName: 'Isochrones',
                    entityName: '',
                    _entityName: '',
                    needAlgorithmId: false,
                    showByDefault: true,
                    order: 100,
                    opacity: 0.5,
                    dayTimeDependent: false,
                    queryable: false,
                    clusterize: false,
                    clickable: true,
                    updateOnFeatureSelection: false,
                    group: 'General',
                    hasBalloon: false,
                };

                const { sources, layers } = entityMapLayersToSourcesAndLayers([
                    {
                        entityMapLayers: [isochroneSource],
                    },
                ]);
                dispatch(addedSources(sources));
                dispatch(addedLayers({ layers, position: 'top' }));

                const { features } = resp;
                dispatch(
                    addedSourceData({
                        sourceId: 'isochrones',
                        data: {
                            type: 'FeatureCollection',
                            features:
                                features && features.length > 0
                                    ? features
                                    : (isochroneData.features as any),
                        },
                    })
                );
            }

            setLoading(false);

            return;
        },
        [presetId, mapboxMapId]
    );

    return (
        <div className="road-route-checker">
            <div className="road-route-checker__form">
                <div className="road-route-checker__header"></div>
                <LoadingBackdrop isVisible={loading} transparent>
                    <div className="entity-editor__header" style={{ marginBottom: '10px' }}>
                        <div className="title">
                            {'Drag the \uD83D\uDD35 blue marker on the map to analyze.'}
                        </div>
                    </div>
                    {markersCoordinates.fromPoint ? (
                        <Form
                            name="entityForm"
                            onSubmit={onFormSubmit}
                            submitBtnTitle={t('route-check.form-submit-btn')}
                            ref={formRef}
                            submitBtnTestId="entityFormSubmitBtn"
                            testId="entityForm"
                        >
                            <Row>
                                <TextField.Labeled
                                    name="startTime"
                                    value={'10:00:00'}
                                    label={'Start time, min'}
                                    type="time"
                                    step="1"
                                    required={true}
                                    style={{ width: 'maxContent' }}
                                    autoFocus
                                />
                            </Row>
                            <Row>
                                <TextField.Labeled
                                    name="distance"
                                    // value={interval}
                                    defaultValue={800}
                                    label={'Distance, m'}
                                    type="number"
                                    required={true}
                                    linkable
                                />
                            </Row>
                            <Row>
                                <MapMarkerField
                                    name={ROAD_ROUTE_CHECKER_MARKER_NAME.fromPoint}
                                    defaultCoordinates={markersCoordinates.fromPoint}
                                    fieldGroupClassName="map-marker-form-field"
                                />
                            </Row>
                        </Form>
                    ) : null}
                </LoadingBackdrop>
            </div>
        </div>
    );
};

export default IsochronesViewer;
